import React, { ChangeEvent, useCallback, useMemo } from 'react';
import NumberFormat                                 from 'react-number-format';
import styled                                       from 'styled-components';
import TextField                                    from '@material-ui/core/TextField';
import withWidth                                    from '@material-ui/core/withWidth';

export interface ITextInputGrayProps {
  type?              : string;
  label?             : string;
  id?                : string;
  field              : any;
  form               : any;
  error?             : boolean;
  disabled?          : boolean;
  decimalScale?      : number;
  fixedDecimalScale? : boolean;
  placeholder?       : string;
  multiline?         : boolean;
  width              : string;
  thousandSeparator? : boolean;
  handleBlur?        : void;
  stripChars         : string;
  other?             : any;
}

const TextInputWrapper = styled.div`
  > span {
    font-weight : 300;
    position    : absolute;
    left        : 6px;
    top         : 6px;
  }
`;

const StyledTextField = styled(TextField)`
  height : ${({ multiline }): any => !multiline && '35px'};
  margin : 0;

  > div {
    background-color: #F5F5F5;

    ${({ multiline }): any => multiline && `
      padding: 8px 10px 10px 12px;
    `}
  }

  fieldset {
    border-radius : 5px;
    border        : 1px solid #DDDDDD;
  }

  input {
    color            : ${({ theme: { textColor } }): string => textColor.gray};
    padding          : 6px 14px 7px ${({ labelicon }: any): string => (labelicon ? '20px' : '14px')};
    background-color : #F5F5F5;
  }

  textarea {
    color : ${({ theme: { textColor } }): string => textColor.gray};
  }
` as any;

/*
  to solve issue input loses focus after each key input
  https://github.com/s-yadav/react-number-format/issues/233
*/
const StyledTextFieldWrapper = (inputProps: any): JSX.Element => (
  <StyledTextField {...inputProps} />
);

const TextInputGrayComponent = ({
  field,
  form,
  type,
  label,
  id,
  disabled          = false,
  fixedDecimalScale = true,
  decimalScale,
  placeholder,
  multiline,
  width,
  thousandSeparator,
  handleBlur,
  stripChars,
  ...other
}: ITextInputGrayProps): JSX.Element => {
  const onBlur = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (e.target.value === '') {
        form.setFieldValue(field.name, null);
      }

      field.onBlur(e);
    },
    [field, form],
  );

  const onValueChange = useCallback(
    (value: any): void => {
      form.setFieldValue(field.name, value.floatValue);
    },
    [field.name, form],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const stripRegex = new RegExp(stripChars, 'g');
      let value = e.target.value.replace(stripRegex, '');

      if (field.name === 'vin') {
        value = value.toUpperCase();
      }

      form.setFieldTouched(field.name, true);

      e.target.value = value;
      field.onChange(e);
    },
    [stripChars, field, form],
  );

  const fieldProps = useMemo(
    (): any => {
      const { onChange, ...restField } = field;

      const props = {
        ...restField,
        id,
        disabled,
        placeholder,
        type      : 'text',
        variant   : 'outlined',
        labelicon : label,
        fullWidth : true,
        onBlur    : handleBlur || onBlur,
        value     : field.value ?? '',
        ...other,
      };

      if (type === 'number') {
        props.thousandSeparator = thousandSeparator;
        props.onValueChange     = onValueChange;
        props.customInput       = StyledTextFieldWrapper;
        props.decimalScale      = decimalScale;
        props.fixedDecimalScale = fixedDecimalScale;
      } else {
        props.multiline = multiline;
        props.rows      = width === 'xs' ? '6' : '8';
        props.onChange  = handleChange;
      }

      return props;
    },
    [
      disabled,
      field,
      handleBlur,
      handleChange,
      id,
      label,
      multiline,
      onBlur,
      onValueChange,
      other,
      placeholder,
      decimalScale,
      thousandSeparator,
      fixedDecimalScale,
      type,
      width,
    ],
  );

  const renderComponent = useMemo(
    (): any => {
      if (type === 'number') {
        return <NumberFormat {...fieldProps} />;
      }

      return <StyledTextField {...fieldProps} />;
    },
    [fieldProps, type],
  );

  return (
    <TextInputWrapper>
      {renderComponent}
      <span>{label}</span>
    </TextInputWrapper>
  );
};

export const TextInputGray = withWidth()(TextInputGrayComponent);
